<template>
  <Card class="mb-0 gap-2 py-3" data-testid="dashboard-recruiting-funnel-card">
    <div class="border-b border-gray-200 px-4 pb-2">
      <div class="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <CardTitle>Recruiting pipeline</CardTitle>

        <Popover v-model:open="timeframePopoverOpen">
          <PopoverTrigger as-child>
            <button class="dashboard-filter-trigger" data-testid="dashboard-recruiting-timeframe-trigger">
              <CalendarRange class="h-3.5 w-3.5" />
              <span>{{ timeframeLabel }}</span>
              <ChevronDown class="h-3.5 w-3.5 text-gray-400" />
            </button>
          </PopoverTrigger>

          <PopoverContent align="end" class="w-72 p-1.5" :side-offset="8">
            <div class="space-y-1">
              <button
                v-for="option in timelineOptions"
                :key="option"
                class="dashboard-filter-row"
                :class="{ 'dashboard-filter-row-active': metricsTimeline === option }"
                @click="selectTimeline(option)"
              >
                <span>{{ option }}</span>
                <CheckIcon v-if="metricsTimeline === option" class="h-3.5 w-3.5" />
              </button>
            </div>

            <div class="mt-1 border-t border-gray-100 px-2 pt-2.5">
              <p class="mb-2 text-[11px] font-medium uppercase tracking-[0.08em] text-gray-500">Custom range</p>

              <div class="space-y-2">
                <DatePicker
                  :model-value="customStart || undefined"
                  @update:model-value="emit('update:customStart', $event ?? null)"
                />
                <DatePicker
                  :model-value="customEnd || undefined"
                  @update:model-value="emit('update:customEnd', $event ?? null)"
                />
              </div>

              <Button
                class="mt-2 w-full"
                variant="dark"
                :disabled="!customStart || !customEnd"
                @click="applyCustomTimeframe"
              >
                Apply custom range
              </Button>
            </div>
          </PopoverContent>
        </Popover>
      </div>
    </div>

    <CardContent class="px-0">
      <div v-for="step in steps" :key="step.label" class="border-b border-gray-200 px-4 py-2.5 last:border-b-0">
        <div class="flex items-center justify-between gap-3">
          <p class="text-sm font-medium text-slate-900">{{ step.label }}</p>
          <div class="flex items-center gap-2">
            <p class="text-xl font-semibold text-slate-900">{{ step.value }}</p>
            <Badge v-if="step.rateLabel" variant="gray" class="px-1.5 py-0 text-[11px] font-medium normal-case">
              {{ step.rateLabel }}
            </Badge>
          </div>
        </div>

        <div class="mt-2 h-2 overflow-hidden rounded-full bg-slate-100">
          <div class="h-full rounded-full" :class="step.barClass" :style="{ width: `${step.width}%` }"></div>
        </div>
      </div>
    </CardContent>
  </Card>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';
import type { DashboardMetricsTimeline, DashboardRecruitingFunnel } from '@/api/dashboard';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardTitle } from '@/components/ui/card';
import { DatePicker } from '@/components/ui/date_picker';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { CalendarRange, CheckIcon, ChevronDown } from 'lucide-vue-next';

const props = defineProps<{
  funnel: DashboardRecruitingFunnel;
  metricsTimeline: DashboardMetricsTimeline;
  customTimeframeLabel: string;
  customStart: string | null;
  customEnd: string | null;
}>();

const emit = defineEmits<{
  setTimeline: [value: DashboardMetricsTimeline];
  'update:customStart': [value: string | null];
  'update:customEnd': [value: string | null];
  applyCustomTimeframe: [];
}>();

const timeframePopoverOpen = ref(false);
const timelineOptions: DashboardMetricsTimeline[] = ['This Month', 'Last Month', 'This Year', 'All Time'];

const timeframeLabel = computed(() =>
  props.metricsTimeline === 'Custom' ? props.customTimeframeLabel : props.metricsTimeline
);

const selectTimeline = (timeline: DashboardMetricsTimeline) => {
  if (timeline === 'Custom') return;

  timeframePopoverOpen.value = false;
  emit('setTimeline', timeline);
};

const applyCustomTimeframe = () => {
  timeframePopoverOpen.value = false;
  emit('applyCustomTimeframe');
};

const rateLabel = (rate: number, denominator: number) => (denominator > 0 ? `${(rate * 100).toFixed(0)}%` : '—');

const steps = computed(() => {
  const maxValue = Math.max(
    props.funnel.sourced,
    props.funnel.phone_screened,
    props.funnel.interviewed,
    props.funnel.hired,
    1
  );

  return [
    {
      label: 'Candidates sourced',
      value: props.funnel.sourced,
      rateLabel: null,
      width: 100,
      barClass: 'bg-slate-800',
    },
    {
      label: 'Phone screened',
      value: props.funnel.phone_screened,
      rateLabel: rateLabel(props.funnel.phone_screen_rate, props.funnel.sourced),
      width: Math.max((props.funnel.phone_screened / maxValue) * 100, props.funnel.phone_screened > 0 ? 24 : 0),
      barClass: 'bg-slate-700',
    },
    {
      label: 'Interviewed',
      value: props.funnel.interviewed,
      rateLabel: rateLabel(props.funnel.interview_rate, props.funnel.sourced),
      width: Math.max((props.funnel.interviewed / maxValue) * 100, props.funnel.interviewed > 0 ? 20 : 0),
      barClass: 'bg-slate-500',
    },
    {
      label: 'Hired',
      value: props.funnel.hired,
      rateLabel: rateLabel(props.funnel.hire_rate, props.funnel.sourced),
      width: Math.max((props.funnel.hired / maxValue) * 100, props.funnel.hired > 0 ? 16 : 0),
      barClass: 'bg-slate-400',
    },
  ];
});
</script>

<style scoped>
@reference "tailwindcss";

.dashboard-filter-trigger {
  @apply inline-flex items-center gap-1.5 rounded-md px-0 py-0 text-sm font-medium text-gray-500 transition-colors hover:text-gray-900;
}

.dashboard-filter-row {
  @apply flex w-full items-center justify-between rounded-md px-2 py-1.5 text-sm text-gray-600 transition-colors hover:bg-gray-50 hover:text-gray-900;
}

.dashboard-filter-row-active {
  @apply bg-gray-100 font-medium text-gray-900;
}
</style>
